import React, { useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { ControlledInput } from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';

import { Control, FieldErrors, UseFormSetValue, UseFormSetError, UseFormClearErrors, useWatch } from 'react-hook-form';
import { useBarcodeValidation } from 'src/hooks/useBarcodeValidation';
import styles from './ProductPackaging.module.scss';
import tableStyles from '../Quality/QualityCheckTable/QualityCheckTable.module.scss';

import { createEmptyPackaging } from './ProductPackagingHelpers';
import { ProductFormValues } from '../ProductFormTypes';
import { useDuplicateBarcodeValidation } from '../../../../hooks/useDuplicateBarcodeValidation';

const ProductPackagingEdit: React.FC<{
  editModeActive: boolean;
  idx: number;
  setExpandedIndex?: (index: number | null) => void;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
  setError: UseFormSetError<ProductFormValues>;
  clearErrors: UseFormClearErrors<ProductFormValues>;
}> = ({ editModeActive, setExpandedIndex, idx, control, setValue, errors, setError, clearErrors }) => {
  const packagings = useWatch({ control, name: 'productPackagings' }) ?? [];
  const packaging = packagings?.[idx];
  const barcode = useWatch({ control, name: `productPackagings.${idx}.barcode` }) || '';

  const { t } = useTranslation();

  if (!packaging) {
    throw new Error(`Packaging with index ${idx} not found`);
  }

  useBarcodeValidation({
    barcode,
    fieldPath: `productPackagings.${idx}.barcode`,
    setError,
    clearErrors,
  });

  useDuplicateBarcodeValidation({
    packagings,
    setError,
    clearErrors,
    t,
  });

  const deleteDisabled = !editModeActive;
  return (
    <>
      <div className="flex items-center gap-16 mb-16">
        <ControlledInput
          control={control}
          name={`productPackagings.${idx}.name`}
          error={!!errors.productPackagings?.[idx]?.name}
          errorMessage={errors.productPackagings?.[idx]?.name?.message}
          isInfoDisplay
          disabled={!editModeActive}
          className="col-span-2"
          type="string"
          placeholder={t('product_packaging.name')}
          label={`${t('product_packaging.name')} *`}
          maxLength={60}
        />
        <ControlledInput
          maxLength={30}
          control={control}
          name={`productPackagings.${idx}.barcode`}
          error={!!errors.productPackagings?.[idx]?.barcode}
          errorMessage={errors.productPackagings?.[idx]?.barcode?.message}
          isInfoDisplay
          disabled={!editModeActive}
          className="col-span-2"
          type="string"
          placeholder={t('product_packaging.barcode')}
          label={`${t('product_packaging.barcode')} *`}
        />
        <ControlledInput
          control={control}
          name={`productPackagings.${idx}.quantity`}
          error={!!errors.productPackagings?.[idx]?.quantity}
          errorMessage={errors.productPackagings?.[idx]?.quantity?.message}
          isInfoDisplay
          disabled={!editModeActive}
          className="col-span-2"
          type="number"
          maxLength={10}
          min={0}
          placeholder={t('product_packaging.quantity')}
          label={`${t('product_packaging.quantity')} *`}
        />
      </div>

      <div className="flex justify-end mt-16 gap-8">
        <Button
          onClick={() => {
            const newPackagings = structuredClone(packagings);
            newPackagings.splice(idx, 1);
            setValue('productPackagings', newPackagings);
            if (setExpandedIndex) {
              setExpandedIndex(null);
            }
          }}
          tone={ButtonToneProps.ERROR}
          title={t('product_packaging.delete')}
          disabled={deleteDisabled}
        />
        <Button
          onClick={() => {
            setExpandedIndex?.(null);
          }}
          tone={ButtonToneProps.SECONDARY}
          title={t('general.close')}
        />
      </div>
    </>
  );
};

const ProductPackagingTable: React.FC<{
  editModeActive: boolean;
  expandedIndex: number | null;
  onExpandToggleClick: (idx: number) => void;
  setExpandedIndex?: (idx: number | null) => void;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
  setError: UseFormSetError<ProductFormValues>;
  clearErrors: UseFormClearErrors<ProductFormValues>;
}> = ({
  expandedIndex,
  onExpandToggleClick,
  editModeActive,
  setExpandedIndex,
  control,
  errors,
  setValue,
  setError,
  clearErrors,
}) => {
  const { t } = useTranslation();
  const packagings = useWatch({ control, name: 'productPackagings' }) ?? [];

  return (
    <div className={classNames(tableStyles.root, 'mb-16')}>
      <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated">
        <thead>
          <tr>
            <th className="typo-text-12 text-faded text-left py-2 px-4">{t('product_packaging.name')}</th>
            <th className="typo-text-12 text-faded text-left py-2 px-4">{t('product_packaging.barcode')}</th>
            <th className="typo-text-12 text-faded text-left py-2 px-4">{t('product_packaging.quantity')}</th>
            <th className="typo-text-12 text-faded text-left py-2 px-4" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {packagings.length === 0 && (
            <tr>
              <td colSpan={4} className="typo-text-14 text-faded text-center py-2 px-4">
                {t('product_packaging.no_packagings')}
              </td>
            </tr>
          )}
          {packagings.map((packaging, idx) => (
            <React.Fragment key={packaging.id}>
              <tr
                key={packaging.id}
                className={`hover:surface-elevated-more ${tableStyles.row}`}
                onClick={() => {
                  onExpandToggleClick(idx);
                }}>
                <td className="typo-text-14 text-full py-2 px-4">{packaging.name || '–'}</td>
                <td className="typo-text-14 text-full py-2 px-4">{packaging.barcode || '–'}</td>
                <td className="typo-text-14 text-full py-2 px-4">{packaging.quantity}</td>
                <td>
                  {' '}
                  <Button
                    className="ml-auto"
                    tone={ButtonToneProps.SECONDARY}
                    icon={expandedIndex === idx ? 'accordion-arrow-up' : 'accordion-arrow-down'}
                    onClick={() => {
                      onExpandToggleClick(idx);
                    }}
                  />
                </td>
              </tr>
              {expandedIndex === idx && (
                <tr key={`${packaging.id}-expanded`} className="border-subtle-bottom">
                  <td colSpan={5} aria-label="Edit Product Packaging">
                    <ProductPackagingEdit
                      control={control}
                      setValue={setValue}
                      errors={errors}
                      editModeActive={editModeActive}
                      idx={idx}
                      setExpandedIndex={setExpandedIndex}
                      setError={setError}
                      clearErrors={clearErrors}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export interface ProductPackagingProps {
  editModeActive: boolean;
  control: Control<ProductFormValues>;
  errors: FieldErrors<ProductFormValues>;
  setValue: UseFormSetValue<ProductFormValues>;
  setError: UseFormSetError<ProductFormValues>;
  clearErrors: UseFormClearErrors<ProductFormValues>;
}

const ProductPackaging: React.FC<ProductPackagingProps> = ({
  editModeActive,
  control,
  errors,
  setValue,
  setError,
  clearErrors,
}) => {
  const { t } = useTranslation();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const packagings = useWatch({ control, name: 'productPackagings' }) ?? [];

  return (
    <div className={classNames(styles.root)}>
      <div className="w-full">
        {packagings && (
          <>
            <ProductPackagingTable
              control={control}
              errors={errors}
              setValue={setValue}
              setError={setError}
              clearErrors={clearErrors}
              editModeActive={editModeActive}
              expandedIndex={expandedIndex}
              onExpandToggleClick={idx => {
                if (expandedIndex === idx) {
                  setExpandedIndex(null);
                } else {
                  setExpandedIndex(idx);
                }
              }}
              setExpandedIndex={setExpandedIndex}
            />

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  const newPackagings = structuredClone(packagings ?? []);
                  newPackagings.push(createEmptyPackaging());
                  setValue('productPackagings', newPackagings);
                  setExpandedIndex(newPackagings.length - 1);
                }}
                tone={ButtonToneProps.SECONDARY}
                title={t('product_packaging.create')}
                disabled={!editModeActive}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductPackaging;
